import { useCallback, useRef } from 'react';

/**
 * Runs an async callback such that at most one invocation is ever in flight.
 *
 * Calls arriving while one is running are collapsed into a single follow-up
 * that starts once it settles, so a burst produces two runs rather than one
 * per event. Throttling alone cannot give this: it bounds how often a run
 * starts, not how many overlap, so any source firing faster than the callback
 * settles still stacks concurrent work.
 */
export function useCoalescedCallback(
    callback: () => Promise<unknown> | unknown,
): () => void {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    const inFlightRef = useRef(false);
    const pendingRef = useRef(false);

    const run = useCallback((): void => {
        if (inFlightRef.current) {
            pendingRef.current = true;
            return;
        }
        inFlightRef.current = true;
        let settled: Promise<unknown>;
        try {
            settled = Promise.resolve(callbackRef.current());
        } catch (error) {
            // Disarm before rethrowing so the throw still reaches the caller
            // without stopping every later run. Anything queued stays queued —
            // nothing ran, so it is still owed.
            inFlightRef.current = false;
            throw error;
        }
        // A rejection is dropped on purpose — the caller's own error state
        // reports it, and leaving it unhandled would wedge the guard.
        void settled
            .catch(() => undefined)
            .finally(() => {
                inFlightRef.current = false;
                if (pendingRef.current) {
                    pendingRef.current = false;
                    try {
                        run();
                    } catch {
                        // A drained refresh has no caller to receive a throw,
                        // and letting it escape here only becomes an unhandled
                        // rejection. The guard is already disarmed.
                    }
                }
            });
    }, []);

    return run;
}
